import React, { FC, useMemo, useCallback, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Card,
  Checkbox,
} from "@mui/material";
import FormTextInput from "./FormTextInput";
import FormCheckbox from "./FormCheckBox";
import { figureFormatter } from "../../../utils/figure_formatter";
import { IFormField } from "../../../utils/form_factory";

interface IFee {
  id: string;
  name: string;
  paymentType: string;
  chargeAmount: number;
}

interface IFeeForm {
  id: string;
  name: string;
  paymentType: string;
  selected: boolean;
  originalRate: number;
  rateAmount: number | "";
}

interface IFeeData {
  fee: string;
  rateamount: number | "";
}

interface TableFormFieldProps extends IFormField {
  name: string;
  label: any;
  formControl: any;
  feesData?: IFee[];
  readOnly?: boolean;
  setTotalFees?: (total: any) => void;
}

const TableFormField: FC<TableFormFieldProps> = ({
  name,
  label,
  formControl,
  feesData = [],
  readOnly = false,
  setTotalFees,
}) => {
  const { values, setFieldValue } = formControl;
  const currentFeeEntries: IFeeData[] = values[name] || [];

  // Track if we've initialized and if user has interacted
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasUserInteraction, setHasUserInteraction] = useState(false);

  //SAFE setFieldValue (prevents infinite loop)
  const safeSetFieldValue = useCallback(
    (fieldName: string, value: any) => {
      const current = formControl.values[fieldName];
      if (JSON.stringify(current) === JSON.stringify(value)) return;
      formControl.setFieldValue(fieldName, value);
      setTotalFees &&
        setTotalFees(value?.reduce((a: any, b: any) => a + b?.rateamount, 0));
    },
    [formControl],
  );

  useEffect(() => {
    if (!feesData.length || hasInitialized) return;

    const initialEntries = feesData.map((fee: IFee) => ({
      fee: fee.id,
      rateamount: fee.chargeAmount,
    }));

    safeSetFieldValue(name, initialEntries);
    setHasInitialized(true);
  }, [feesData.length, hasInitialized, name, safeSetFieldValue]);

  // Transform feesData to feeEntries format for display
  const transformedFeeEntries = useMemo((): IFeeForm[] => {
    if (!feesData || feesData.length === 0) {
      return [];
    }

    return feesData.map((fee: IFee) => {
      const existingEntry = currentFeeEntries.find(
        (entry: IFeeData) => entry.fee === fee.id,
      );

      // If user has interacted, use the actual selected state
      // Otherwise, all are selected by default
      const isSelected = hasUserInteraction ? !!existingEntry : true; // All selected by default until user interacts

      return {
        id: fee.id,
        name: fee.name,
        paymentType: fee.paymentType,
        selected: isSelected,
        originalRate: fee.chargeAmount,
        rateAmount: existingEntry?.rateamount ?? fee.chargeAmount,
      };
    });
  }, [feesData, currentFeeEntries, hasUserInteraction]);

  // Create simplified data for form storage
  const createSimplifiedData = useCallback(
    (entries: IFeeForm[]): IFeeData[] => {
      return entries
        .filter((entry) => entry.selected)
        .map((entry) => ({
          fee: entry.id,
          rateamount: entry.rateAmount,
        }));
    },
    [],
  );

  // Handle select all checkbox
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setHasUserInteraction(true);

      const updatedEntries = transformedFeeEntries.map((fee: IFeeForm) => ({
        ...fee,
        selected: checked,
        rateAmount: checked
          ? fee.rateAmount || fee.originalRate
          : fee.originalRate,
      }));

      const simplifiedData = createSimplifiedData(updatedEntries);
      setFieldValue(name, simplifiedData);
      setTotalFees &&
        setTotalFees(
          simplifiedData?.reduce((a: any, b: any) => a + b?.rateamount, 0),
        );
    },
    [transformedFeeEntries, name, setFieldValue, createSimplifiedData],
  );

  // Check if all are selected
  const allSelected = useMemo(() => {
    return (
      transformedFeeEntries.length > 0 &&
      transformedFeeEntries.every((f: IFeeForm) => f.selected)
    );
  }, [transformedFeeEntries]);

  // Check if some are selected (for indeterminate state)
  const someSelected = useMemo(() => {
    return (
      transformedFeeEntries.some((f: IFeeForm) => f.selected) && !allSelected
    );
  }, [transformedFeeEntries, allSelected]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" gutterBottom>
        {label}
      </Typography>
      <TableContainer
        component={Card}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Table sx={{ minWidth: 500 }} size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "action.hover" }}>
              <TableCell sx={{ fontWeight: "bold" }}>
                <Checkbox
                  aria-label="select all fees"
                  checked={allSelected}
                  indeterminate={someSelected}
                  disabled={transformedFeeEntries.length === 0 || readOnly}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", width: "25%" }}>
                Fee Name
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", width: "25%" }}>
                Original Amount
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                New Amount
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            <TableEntryForm
              feeEntries={transformedFeeEntries}
              formControl={formControl}
              name={name}
              readonly={readOnly}
              currentFeeEntries={currentFeeEntries}
              createSimplifiedData={createSimplifiedData}
              setHasUserInteraction={setHasUserInteraction}
              setTotalFees={setTotalFees}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

interface TableEntryFormProps {
  feeEntries: IFeeForm[];
  formControl: any;
  name: string;
  readonly: boolean;
  currentFeeEntries: IFeeData[];
  createSimplifiedData: (entries: IFeeForm[]) => IFeeData[];
  setHasUserInteraction: (value: boolean) => void;
  setTotalFees?: (value: any) => void;
}

const TableEntryForm: FC<TableEntryFormProps> = ({
  feeEntries,
  formControl,
  name,
  readonly,
  currentFeeEntries,
  createSimplifiedData,
  setHasUserInteraction,
  setTotalFees,
}) => {
  // Get the actual stored fee data for this index
  const getStoredFee = useCallback(
    (index: number) => {
      const feeEntry = feeEntries[index];
      return currentFeeEntries.find((entry) => entry.fee === feeEntry?.id);
    },
    [feeEntries, currentFeeEntries],
  );

  // Fixed getFormControl function
  const getFormControl = useCallback(
    (index: number, field: string) => {
      const feeEntry = feeEntries[index];
      const storedFee = getStoredFee(index);

      const getValue = () => {
        if (field === "selected") {
          return feeEntry?.selected ?? true;
        }
        if (field === "rateAmount") {
          return storedFee?.rateamount ?? feeEntry?.originalRate ?? "";
        }
        return feeEntry?.[field as keyof IFeeForm];
      };

      const currentValue = getValue();

      return {
        values: { [field]: currentValue },
        handleChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          setHasUserInteraction(true); // Mark that user has interacted

          const feeEntry = feeEntries[index];
          if (!feeEntry) return;

          let newValue: any;

          if (field === "rateAmount") {
            const inputValue = event.target.value;
            if (inputValue === "") {
              newValue = "";
            } else {
              const parsedValue = parseFloat(inputValue);
              newValue = isNaN(parsedValue) ? "" : parsedValue;
            }
          } else if (field === "selected") {
            newValue = event.target.checked;
          } else {
            newValue = event.target.value;
          }

          // Update the specific entry in the array
          const updatedEntries = feeEntries.map((entry, idx) => {
            if (idx === index) {
              const updatedEntry = {
                ...entry,
                [field]: newValue,
              };

              // If unselected, reset rateAmount to original value
              if (field === "selected" && !newValue) {
                updatedEntry.rateAmount = entry.originalRate;
              }

              return updatedEntry;
            }
            return entry;
          });

          const simplifiedData = createSimplifiedData(updatedEntries);
          formControl.setFieldValue(name, simplifiedData);
          setTotalFees &&
            setTotalFees(
              simplifiedData?.reduce((a: any, b: any) => a + b?.rateamount, 0),
            );
        },
        handleBlur: formControl.handleBlur,
        touched: formControl.touched?.[name]?.[index]?.[field],
        errors: formControl.errors?.[name]?.[index]?.[field],
        getFieldMeta: (fieldName: string) => ({
          value: getValue(),
          error: formControl.errors?.[name]?.[index]?.[fieldName],
          touched: formControl.touched?.[name]?.[index]?.[fieldName],
        }),
        setFieldValue: (fieldName: string, value: any) => {
          setHasUserInteraction(true); // Mark that user has interacted

          const updatedEntries = feeEntries.map((entry, idx) => {
            if (idx === index) {
              const updatedEntry = {
                ...entry,
                [fieldName]: value,
              };

              // If unselected, reset rateAmount to original value
              if (fieldName === "selected" && !value) {
                updatedEntry.rateAmount = entry.originalRate;
              }

              return updatedEntry;
            }
            return entry;
          });

          const simplifiedData = createSimplifiedData(updatedEntries);
          formControl.setFieldValue(name, simplifiedData);
          setTotalFees &&
            setTotalFees(
              simplifiedData?.reduce((a: any, b: any) => a + b?.rateamount, 0),
            );
        },
      };
    },
    [
      feeEntries,
      formControl,
      name,
      getStoredFee,
      createSimplifiedData,
      setHasUserInteraction,
    ],
  );

  if (feeEntries.length === 0) {
    return null;
  }

  return (
    <>
      {feeEntries.map((fee, index) => {
        const formControlForCheckbox = getFormControl(index, "selected");
        const formControlForRate = getFormControl(index, "rateAmount");

        if (!formControlForCheckbox || !formControlForRate) {
          return null;
        }

        return (
          <TableRow key={fee.id || index}>
            {/* Selection Checkbox */}
            <TableCell sx={{ paddingLeft: 3.3 }}>
              <FormCheckbox
                name="selected"
                isDisabled={readonly}
                formControl={formControlForCheckbox}
              />
            </TableCell>

            {/* Fee Name Column - Read Only */}
            <TableCell component="th" scope="row">
              <Typography variant="body1" fontWeight="medium">
                {fee.name}
              </Typography>
            </TableCell>

            {/* Original Charge Amount Column - Read Only */}
            <TableCell>
              <Typography
                variant="body1"
                fontWeight="bold"
                color="text.primary"
              >
                {figureFormatter({ figure: fee.originalRate })}
              </Typography>
            </TableCell>

            {/* New Charge Amount Column - Editable */}
            <TableCell>
              <FormTextInput
                name="rateAmount"
                type="number"
                isDisabled={readonly || !fee.selected}
                formControl={formControlForRate}
                value={fee.rateAmount}
                max={fee.originalRate}
              />
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
};

export default TableFormField;
